import React from 'react';
import { Shield, Clock, MapPin, AlertCircle } from 'lucide-react';

interface SecurityProps {
  hideHeader?: boolean;
}

const Security: React.FC<SecurityProps> = ({ hideHeader = false }) => {
  const presenceEvents = [
    {
      id: 1,
      time: '19:42',
      zone: 'Server Room',
      floor: 'Floor 4',
      duration: '8 min',
      paths: 3,
      status: 'authorized',
      note: 'Badge verified: IT Ops maintenance window'
    },
    {
      id: 2,
      time: '21:15',
      zone: 'Executive Suite',
      floor: 'Floor 5',
      duration: '22 min',
      paths: 8,
      status: 'alert',
      note: 'Unauthorized presence. Security notified'
    },
    {
      id: 3,
      time: '22:30',
      zone: 'R&D Lab',
      floor: 'Floor 3',
      duration: '45 min',
      paths: 12,
      status: 'authorized',
      note: 'Badge verified: Engineering team'
    },
    {
      id: 4,
      time: '02:18',
      zone: 'East Wing Corridor',
      floor: 'Floor 2',
      duration: '3 min',
      paths: 2,
      status: 'review',
      note: 'Brief presence. Cleaning crew likely'
    },
    {
      id: 5,
      time: '06:15',
      zone: 'North Wing',
      floor: 'Floor 3',
      duration: '12 min',
      paths: 5,
      status: 'authorized',
      note: 'Early arrival. Badge matched'
    }
  ];

  const currentPresence = [
    { zone: 'Server Room', floor: '4', count: 1, authorized: true },
    { zone: 'R&D Lab', floor: '3', count: 2, authorized: true }
  ];

  const weekPattern = [
    { day: 'Mon', events: 8 },
    { day: 'Tue', events: 6 },
    { day: 'Wed', events: 7 },
    { day: 'Thu', events: 9 },
    { day: 'Fri', events: 12 },
    { day: 'Sat', events: 3 },
    { day: 'Sun', events: 2 }
  ];

  const alertEvents = presenceEvents.filter(e => e.status === 'alert');
  const authorizedEvents = presenceEvents.filter(e => e.status === 'authorized');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'alert':
        return { bg: '#fee', color: '#c33', border: '#fcc' };
      case 'authorized':
        return { bg: 'var(--sca-badge-bg)', color: 'var(--sca-teal)', border: 'var(--sca-teal-muted)' };
      case 'review':
        return { bg: '#fef9e7', color: '#8a7355', border: '#e8dfd0' };
      default:
        return { bg: '#f5f5f5', color: '#666', border: '#ddd' };
    }
  };

  return (
    <div className="space-y-6" style={{ maxWidth: '1600px', margin: '0 auto' }}>
      {!hideHeader && (
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
            Security
          </h2>
          <p style={{ color: 'var(--sca-muted)' }}>
            After-hours presence monitoring. Sample building data, illustrative. Camera-free floor presence.
          </p>
        </div>
      )}

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Shield size={18} style={{ color: 'var(--sca-teal)' }} />
            <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Current Presence</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>
            {currentPresence.reduce((sum, p) => sum + p.count, 0)}
          </div>
          <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>active after-hours</div>
        </div>

        <div className="p-4 rounded-lg border" style={{ backgroundColor: '#fee', borderColor: '#fcc' }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={18} style={{ color: '#c33' }} />
            <span className="text-sm" style={{ color: '#c33' }}>Alerts Today</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: '#c33' }}>
            {alertEvents.length}
          </div>
          <div className="text-xs" style={{ color: '#c33' }}>unauthorized events</div>
        </div>

        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Clock size={18} style={{ color: 'var(--sca-teal)' }} />
            <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Avg Duration</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>
            18 min
          </div>
          <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>per after-hours event</div>
        </div>

        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={18} style={{ color: 'var(--sca-teal)' }} />
            <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Events (Week)</span>
          </div>
          <div className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>
            47
          </div>
          <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>total presence events</div>
        </div>
      </div>

      {/* Hero: After-Hours Timeline */}
      <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
        <div className="mb-4">
          <h3 className="text-xl font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
            After-Hours Presence Timeline: Today
          </h3>
          <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>
            Floor presence events outside business hours (6 PM to 8 AM). Anonymous detection without cameras.
          </p>
        </div>

        <svg width="100%" height="200" viewBox="0 0 900 200" style={{ backgroundColor: '#F9F7F2', borderRadius: '8px' }}>
          {/* Timeline base */}
          <line x1="50" y1="100" x2="850" y2="100" stroke="#e8e4dc" strokeWidth="3" />

          {/* Business hours overlay */}
          <rect x="650" y="20" width="100" height="160" fill="#F9F7F2" opacity="0.8" />
          <text x="700" y="100" textAnchor="middle" style={{ fontSize: '12px', fill: '#999', fontWeight: '600' }}>
            Business Hours
          </text>

          {/* Hour markers */}
          {[18, 21, 0, 3, 6].map((hour, idx) => {
            const x = 100 + idx * 175;
            return (
              <g key={hour}>
                <line x1={x} y1="95" x2={x} y2="105" stroke="#1a1a1a" strokeWidth="2" />
                <text x={x} y="125" textAnchor="middle" style={{ fontSize: '12px', fill: '#666' }}>
                  {hour === 0 ? '12 AM' : hour < 12 ? `${hour} PM` : hour === 12 ? '12 PM' : `${hour - 12} AM`}
                </text>
              </g>
            );
          })}

          {/* Presence events */}
          {presenceEvents.map((event, idx) => {
            const hourNum = parseInt(event.time.split(':')[0]);
            let position;
            if (hourNum >= 18 && hourNum < 24) {
              position = 100 + ((hourNum - 18) / 6) * 175;
            } else if (hourNum >= 0 && hourNum < 8) {
              position = 450 + (hourNum / 8) * 350;
            } else {
              position = 700;
            }

            const statusStyle = getStatusColor(event.status);
            const yOffset = (idx % 2 === 0 ? -40 : 40);

            return (
              <g key={event.id}>
                <circle
                  cx={position}
                  cy={100}
                  r="8"
                  fill={statusStyle.color}
                  stroke={statusStyle.color}
                  strokeWidth="3"
                />
                <line
                  x1={position}
                  y1={100}
                  x2={position}
                  y2={100 + yOffset}
                  stroke={statusStyle.color}
                  strokeWidth="1.5"
                  strokeDasharray="2,2"
                />
                <rect
                  x={position - 40}
                  y={100 + yOffset - 15}
                  width="80"
                  height="30"
                  fill={statusStyle.bg}
                  stroke={statusStyle.border}
                  strokeWidth="1"
                  rx="4"
                />
                <text
                  x={position}
                  y={100 + yOffset - 5}
                  textAnchor="middle"
                  style={{ fontSize: '10px', fill: statusStyle.color, fontWeight: '600' }}
                >
                  {event.zone.split(' ')[0]}
                </text>
                <text
                  x={position}
                  y={100 + yOffset + 8}
                  textAnchor="middle"
                  style={{ fontSize: '9px', fill: statusStyle.color }}
                >
                  {event.time}
                </text>
              </g>
            );
          })}
        </svg>

        <div className="mt-4 flex items-center gap-6 text-xs" style={{ color: 'var(--sca-muted)' }}>
          <div className="flex items-center gap-2">
            <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#c33' }}></div>
            <span>Alert (unauthorized)</span>
          </div>
          <div className="flex items-center gap-2">
            <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: 'var(--sca-teal)' }}></div>
            <span>Authorized (badge verified)</span>
          </div>
          <div className="flex items-center gap-2">
            <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#8a7355' }}></div>
            <span>Under review</span>
          </div>
        </div>
      </div>

      {/* Event List + Current Presence */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Event List */}
        <div className="lg:col-span-2 rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
            After-Hours Events: Today
          </h3>

          <div className="space-y-3">
            {presenceEvents.map((event) => {
              const statusStyle = getStatusColor(event.status);

              return (
                <div
                  key={event.id}
                  className="p-4 rounded-lg border"
                  style={{
                    backgroundColor: statusStyle.bg,
                    borderColor: statusStyle.border
                  }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>
                        {event.zone} ({event.floor})
                      </div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                        {event.time} • Duration: {event.duration} • {event.paths} paths detected
                      </div>
                    </div>
                    <span className="text-xs px-2 py-1 rounded font-medium" style={{
                      backgroundColor: statusStyle.color,
                      color: 'white'
                    }}>
                      {event.status}
                    </span>
                  </div>

                  <div className="text-sm" style={{ color: statusStyle.color === '#c33' ? '#c33' : 'var(--sca-ink)' }}>
                    {event.note}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Current Presence + Week Pattern */}
        <div className="space-y-6">
          <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
              Current Presence
            </h3>

            {currentPresence.length > 0 ? (
              <div className="space-y-3">
                {currentPresence.map((presence, idx) => (
                  <div key={idx} className="p-3 rounded-lg" style={{ backgroundColor: 'var(--sca-badge-bg)' }}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium" style={{ color: 'var(--sca-ink)' }}>
                        {presence.zone}
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded" style={{
                        backgroundColor: presence.authorized ? 'var(--sca-teal)' : '#c33',
                        color: 'white'
                      }}>
                        {presence.authorized ? 'Verified' : 'Alert'}
                      </span>
                    </div>
                    <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                      Floor {presence.floor} • {presence.count} present
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-center py-6" style={{ color: 'var(--sca-muted)' }}>
                No current after-hours presence
              </div>
            )}
          </div>

          <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
              Week Pattern
            </h3>

            <div className="space-y-2">
              {weekPattern.map((day) => {
                const maxEvents = Math.max(...weekPattern.map(d => d.events));
                const percent = (day.events / maxEvents) * 100;

                return (
                  <div key={day.day}>
                    <div className="flex justify-between items-center mb-1 text-xs">
                      <span style={{ color: 'var(--sca-ink)' }}>{day.day}</span>
                      <span style={{ color: 'var(--sca-muted)' }}>{day.events} events</span>
                    </div>
                    <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'var(--sca-border)' }}>
                      <div
                        className="h-2 rounded-full"
                        style={{
                          width: `${percent}%`,
                          backgroundColor: 'var(--sca-teal)'
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 text-xs" style={{ color: 'var(--sca-muted)' }}>
              Friday peaks typical (end-of-week work). Weekend minimal (2-3 events).
            </div>
          </div>
        </div>
      </div>

      {/* Implementation Note */}
      <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--sca-hover)' }}>
        <div className="text-sm font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Privacy-Forward Security
        </div>
        <div className="text-xs space-y-1" style={{ color: 'var(--sca-muted)' }}>
          <p>
            <strong>Floor sensor presence:</strong> Anonymous detection of movement and occupancy. No cameras, no facial recognition, no tracking of individuals.
          </p>
          <p>
            <strong>Badge correlation:</strong> After-hours events can be cross-referenced with badge access logs to verify authorized presence.
          </p>
          <p>
            <strong>Partner-friendly:</strong> Flooring specification partners (Tarkett, Mannington) can position as camera-free security layer for CRE and facilities teams.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Security;
